/**
 * CookieConsentModal Component
 *
 * GDPR/CCPA compliant cookie consent popup that also unlocks Bronze tier.
 * Displayed 6 seconds after page load for new visitors.
 *
 * Features:
 * - Matches ManualEntryModal styling exactly
 * - "Accept All" enables Bronze tier + analytics
 * - "Reject All" keeps user as Guest (essential only)
 * - Links to Privacy Policy
 * - No click-outside-to-close (consent must be explicit)
 *
 * Legal Compliance:
 * - Equal prominence for Accept/Reject buttons
 * - Clear explanation of cookie purposes
 * - Link to full Privacy Policy
 * - Manage Preferences option
 */

import React, { useState } from "react";
import {
  setConsentState,
  updateGoogleAnalyticsConsent,
} from "../lib/cookieConsent";
import { registerBronze, getDeviceToken } from "../lib/auth";

// =============================================================================
// Types
// =============================================================================

export interface CookieConsentModalProps {
  /** Callback when user makes a choice (accept or reject) */
  onComplete: (accepted: boolean) => void;
  /** Optional callback for "Manage Preferences" - if not provided, link is hidden */
  onManagePreferences?: () => void;
}

// =============================================================================
// Copy Constants
// =============================================================================

const COPY = {
  title: "Unlock Bronze Tier",
  body: "We use cookies to provide and improve our service. Accepting all cookies enables Bronze tier with extended scan limits and saved preferences.",
  essential: "Essential cookies are always active.",
  learnMore: "Learn more in our",
  privacyLink: "Privacy Policy",
  buttons: {
    acceptAll: "Accept All",
    rejectAll: "Reject All",
  },
  manageLink: "Manage Preferences",
  // Loading states
  loading: "Setting up...",
};

// =============================================================================
// Component
// =============================================================================

const CookieConsentModal: React.FC<CookieConsentModalProps> = ({
  onComplete,
  onManagePreferences,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Handle "Accept All" - enables Bronze tier
   */
  const handleAcceptAll = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Check if user already has a device token (returning Bronze user)
      const existingToken = getDeviceToken();

      if (!existingToken) {
        // Register new Bronze user
        await registerBronze();
      }

      // Record consent
      setConsentState({
        essential: true,
        functional: true,
        analytics: true,
      });

      // Enable Google Analytics
      updateGoogleAnalyticsConsent(true);

      onComplete(true);
    } catch (err) {
      console.error("Failed to set up Bronze tier:", err);

      // Still record consent even if Bronze registration fails
      // User can retry later or continue as Guest
      setConsentState({
        essential: true,
        functional: true,
        analytics: true,
      });

      updateGoogleAnalyticsConsent(true);

      // Show error but allow continuing
      setError("Could not fully set up Bronze tier. You can try again later.");

      // Complete after a short delay so user sees the message
      setTimeout(() => onComplete(true), 2000);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle "Reject All" - essential cookies only, stays Guest
   */
  const handleRejectAll = () => {
    // Record consent (rejected non-essential)
    setConsentState({
      essential: true,
      functional: false,
      analytics: false,
    });

    // Ensure Google Analytics is disabled
    updateGoogleAnalyticsConsent(false);

    onComplete(false);
  };

  return (
    <div
      style={styles.modalOverlay}
      data-testid="cookie-consent-modal"
      // No onClick handler - consent must be explicit, no click-outside-to-close
    >
      <div style={styles.modalContent}>
        {/* Title */}
        <h3 style={styles.modalTitle}>{COPY.title}</h3>

        {/* Body text */}
        <p style={styles.bodyText}>{COPY.body}</p>

        {/* Essential notice */}
        <p style={styles.essentialText}>{COPY.essential}</p>

        {/* Privacy Policy link */}
        <p style={styles.learnMoreText}>
          {COPY.learnMore}{" "}
          <a
            href="/privacy.html"
            style={styles.privacyLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {COPY.privacyLink}
          </a>
          .
        </p>

        {/* Error message */}
        {error && <p style={styles.errorText}>{error}</p>}

        {/* Buttons */}
        <div style={styles.modalActions}>
          <button
            type="button"
            onClick={handleAcceptAll}
            style={styles.primaryButton}
            disabled={isLoading}
            data-testid="cookie-accept-all"
          >
            {isLoading ? COPY.loading : COPY.buttons.acceptAll}
          </button>
          <button
            type="button"
            onClick={handleRejectAll}
            style={styles.secondaryButton}
            disabled={isLoading}
            data-testid="cookie-reject-all"
          >
            {COPY.buttons.rejectAll}
          </button>
        </div>

        {/* Manage Preferences link (optional) */}
        {onManagePreferences && (
          <button
            type="button"
            onClick={onManagePreferences}
            style={styles.manageLink}
            disabled={isLoading}
            data-testid="cookie-manage-preferences"
          >
            {COPY.manageLink}
          </button>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// Styles - Matching ManualEntryModal exactly
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  modalContent: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "400px",
    width: "90%",
    boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1)",
  },
  modalTitle: {
    margin: "0 0 16px 0",
    fontSize: "20px",
    fontWeight: "600",
    fontFamily: '"Playfair Display", serif',
    color: "#2b1a45",
  },
  bodyText: {
    margin: "0 0 12px 0",
    fontSize: "15px",
    lineHeight: "1.6",
    color: "#374151",
  },
  essentialText: {
    margin: "0 0 12px 0",
    fontSize: "14px",
    color: "#6b7280",
  },
  learnMoreText: {
    margin: "0 0 16px 0",
    fontSize: "14px",
    color: "#6b7280",
  },
  privacyLink: {
    color: "#6a49a8",
    textDecoration: "none",
  },
  errorText: {
    margin: "0 0 16px 0",
    fontSize: "14px",
    color: "#ef4444",
  },
  modalActions: {
    display: "flex",
    gap: "12px",
    marginTop: "20px",
  },
  // Primary button - matches ManualEntryModal exactly
  primaryButton: {
    flex: 1,
    padding: "12px 24px",
    backgroundColor: "#6a49a8",
    color: "#ffffff",
    border: "none",
    borderRadius: "999px",
    fontSize: "14px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  // Secondary button - EQUAL PROMINENCE (GDPR requirement)
  // Same size and visual weight as primary, just different colors
  secondaryButton: {
    flex: 1,
    padding: "10px 22px",
    backgroundColor: "#ffffff",
    color: "#6a49a8",
    border: "2px solid #6a49a8",
    borderRadius: "999px",
    fontSize: "14px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  manageLink: {
    display: "block",
    width: "100%",
    marginTop: "16px",
    padding: "8px",
    backgroundColor: "transparent",
    color: "#6b7280",
    border: "none",
    fontSize: "14px",
    textDecoration: "underline",
    cursor: "pointer",
    textAlign: "center",
  },
};

export default CookieConsentModal;
